/**
 * Timeline view with colored thread lanes for shipment events.
 *
 * Thin wrapper around ShipmentTimelineRenderer that handles:
 * - Data fetching via useShipmentData
 * - Collapsible Reminders / ShipmentDetails / Timeline sections
 * - Debug FAB
 * - DobbyChatWrapper with scroll-to-email integration
 *
 * @example
 * <ShipmentTimelineWithThreadLines
 *   shipmentId="abc123"
 *   numberOfLanes={4}
 *   laneThickness={10}
 * />
 */

import { useState } from "react";
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

import { ShipmentDetails } from "../ShipmentDetails";
import DobbyChatWrapper from "../../components/Chat/DobbyChatWrapper";
import { CollapsibleSection } from "../../components/CollapsibleSection";
import type { ShipmentTimelineWithThreadLinesProps } from "./types";
import { useShipmentData } from "./hooks";
import { DebugMenu } from "./components";
import { downloadAttachment } from "./utils";
import { ShipmentTimelineRenderer } from "./ShipmentTimelineRenderer";

export function ShipmentTimelineWithThreadLines({
  shipmentId,
  updates,
  emailSource,
  includeInternalReminders = false,
  numberOfLanes = 4,
  laneThickness = 10,
  laneSpacing = 40,
  lanesLeftOffset = 10,
  bubbleWidth = 50,
  bubbleBaseOffset = 0,
  bubbleReplyOffsetPercent = 0,
  dashedLineLength = 16,
  bottomPadding = 68,
  dateColumnWidth = 50,
  laneColors,
  otherThreadsLaneColor,
  isAdmin = false,
  filtersCollapsible = false,
  inlineDates = false,
  showEventCategories = false,
  EmailThreadComponent,
  RemindersComponent,
  onOpenEmail,
}: ShipmentTimelineWithThreadLinesProps) {
  // Data fetching
  const { events, shipment, loading, error, refetch } = useShipmentData(
    shipmentId,
    includeInternalReminders,
  );

  // Collapse state for all sections (only Timeline expanded by default)
  const [remindersExpanded, setRemindersExpanded] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [timelineExpanded, setTimelineExpanded] = useState(true);

  // Scroll-to-email state for chat citation integration
  const [scrollToEmailId, setScrollToEmailId] = useState<string | null>(null);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={refetch}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      className="ShipmentTimelineWithThreadLines"
      sx={{
        pt: 0,
        pb: 2,
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Reminders Section - Collapsible */}
      {RemindersComponent &&
        shipment?.reminders &&
        shipment.unresolvedReminders > 0 && (
          <>
            <CollapsibleSection
              title="Reminders"
              count={shipment.unresolvedReminders}
              expanded={remindersExpanded}
              onToggle={() => setRemindersExpanded(!remindersExpanded)}
              icon={<WarningIcon sx={{ fontSize: "1.25rem", color: "#f57c00" }} />}
            >
              <Box sx={{ pt: 1, px: 2 }}>
                <RemindersComponent
                  shipmentId={shipmentId}
                  onEmailOpen={
                    onOpenEmail ??
                    ((emailId: string) => setScrollToEmailId(emailId))
                  }
                  reminders={shipment.reminders}
                />
              </Box>
            </CollapsibleSection>
            <Divider sx={{ mt: 0 }} />
          </>
        )}

      {/* Shipment Details - Collapsible */}
      {shipment && (
        <>
          <CollapsibleSection
            title="Shipment Details"
            expanded={detailsExpanded}
            onToggle={() => setDetailsExpanded(!detailsExpanded)}
          >
            <Box sx={{ pt: 1, px: 2 }}>
              <ShipmentDetails
                shipment={shipment}
                updates={updates}
                emailSource={emailSource}
                maxVisibleDetails={10}
                hideTitle
              />
            </Box>
          </CollapsibleSection>
        </>
      )}

      {/* Timeline Section - Collapsible */}
      <CollapsibleSection
        title={`Timeline (${events.length} emails)`}
        expanded={timelineExpanded}
        onToggle={() => setTimelineExpanded(!timelineExpanded)}
      >
        {/* content rendered outside Collapse for scroll behavior */}
      </CollapsibleSection>

      {/* Timeline Content */}
      {timelineExpanded && (
        <Box sx={{ pl: 2, flex: 1, minHeight: 0, overflow: "hidden" }}>
          <ShipmentTimelineRenderer
            events={events}
            numberOfLanes={numberOfLanes}
            laneThickness={laneThickness}
            laneSpacing={laneSpacing}
            lanesLeftOffset={lanesLeftOffset}
            bubbleWidth={bubbleWidth}
            bubbleBaseOffset={bubbleBaseOffset}
            bubbleReplyOffsetPercent={bubbleReplyOffsetPercent}
            dashedLineLength={dashedLineLength}
            bottomPadding={bottomPadding}
            dateColumnWidth={dateColumnWidth}
            laneColors={laneColors}
            otherThreadsLaneColor={otherThreadsLaneColor}
            filtersCollapsible={filtersCollapsible}
            inlineDates={inlineDates}
            showEventCategories={showEventCategories}
            onDownloadAttachment={downloadAttachment}
            onOpenEmail={onOpenEmail}
            EmailThreadComponent={EmailThreadComponent}
            scrollToEmailId={scrollToEmailId}
            onScrollToEmailHandled={() => setScrollToEmailId(null)}
          />
        </Box>
      )}

      {/* Debug FAB */}
      <DebugMenu shipmentId={shipmentId} events={events} isAdmin={isAdmin} />

      {/* Floating Chat Component */}
      <DobbyChatWrapper
        conversationType="shipment_question"
        extraParams={{
          feedId: shipmentId,
        }}
        onScrollToEmail={(emailId: string) => setScrollToEmailId(emailId)}
      />
    </Box>
  );
}
